"use client"

import type { ReactNode } from "react"
import { OverviewContext, type MembersBoardOverview } from "@ibada/shared-types/overview"

import { BirthdaysTile } from "@/features/dashboard/home-d4/components/birthdays-tile"
import { HomeTileSkeleton, type HomeTileLink } from "@/features/dashboard/home-d4/components/home-tile"
import { MembersTile } from "@/features/dashboard/home-d4/components/members-tile"
import { NewMembersTile } from "@/features/dashboard/home-d4/components/new-members-tile"
import { RecentVisitorsTile } from "@/features/dashboard/home-d4/components/recent-visitors-tile"
import { HomeTileState } from "@/features/dashboard/home-d4/lib/home-grid-layout"
import { BranchesTile } from "@/features/members/components/board/branches-tile"
import { IntegrationTile } from "@/features/members/components/board/integration-tile"
import { useMembersBoardContext, type MembersBoardData } from "@/features/members/components/board/members-board-data"
import { PeopleMixTile } from "@/features/members/components/board/people-mix-tile"
import { ProfilesTile } from "@/features/members/components/board/profiles-tile"
import { ToConfirmTile } from "@/features/members/components/board/to-confirm-tile"
import { useCreateKindAccess } from "@/features/create-overlay/hooks/use-create-kind-access"
import { useCreateOverlay } from "@/features/create-overlay/hooks/use-create-overlay"
import { CreateKind } from "@/features/create-overlay/model/create-kind"
import { useTranslations } from "@/i18n"

/** One height for every board tile, so the engine's masonry rows line up. A tile resized wider keeps it. */
export const MEMBERS_BOARD_TILE_HEIGHT_CLASS = "h-[16.5rem]"

/** Skeleton while loading, the tile once ready, nothing when absent (the registry hides absent tiles anyway). */
function BoardWidget({
  testId,
  render,
}: {
  testId: string
  render: (board: MembersBoardOverview, data: MembersBoardData) => ReactNode
}) {
  const data = useMembersBoardContext()
  if (!data || data.state === HomeTileState.Absent) return null
  return (
    <div className={MEMBERS_BOARD_TILE_HEIGHT_CLASS}>
      {data.state === HomeTileState.Loading || !data.board ? (
        <HomeTileSkeleton testId={`members-board-skeleton-${testId}`} />
      ) : (
        render(data.board, data)
      )}
    </div>
  )
}

/** A tile link, or none where the page does not exist in this context. */
const linkTo = (label: string, href: string | null): HomeTileLink | null => (href ? { label, href } : null)

const withQuery = (href: string | null, query: string) => (href ? `${href}?${query}` : null)

/** Profile links, or none where member profiles are not reachable (the parish context). */
const profileHref = (data: MembersBoardData) =>
  data.href("/members") === null ? null : (memberId: string) => data.href(`/members/${memberId}`) ?? ""

export function BoardMembersWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  const tBoard = useTranslations("people.members.overview.board")
  return (
    <BoardWidget
      testId='members'
      render={(board, data) => (
        <MembersTile
          overview={board}
          membersLink={linkTo(t("members.link"), data.href("/members/list"))}
          addMember={data.can.editMembers && createAccess.isAllowed(CreateKind.Member) ? linkTo(t("members.add"), hrefFor(CreateKind.Member)) : null}
          subtitle={data.context === OverviewContext.Parish ? tBoard("members.subtitleParish") : undefined}
          emptyMessage={data.context === OverviewContext.Parish ? tBoard("members.emptyParish") : undefined}
        />
      )}
    />
  )
}

export function BoardBranchesWidget() {
  const t = useTranslations("people.members.overview.board.branches")
  return (
    <BoardWidget
      testId='branches'
      render={(board, data) => <BranchesTile branches={board.branches} branchesLink={linkTo(t("link"), data.href("/branches"))} />}
    />
  )
}

export function BoardNewMembersWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <BoardWidget
      testId='new-members'
      render={(board, data) => (
        <NewMembersTile
          newMembers={board.newMembers}
          membersLink={linkTo(t("newMembers.link"), data.href("/members/list"))}
          addMember={data.can.editMembers && createAccess.isAllowed(CreateKind.Member) ? linkTo(t("members.add"), hrefFor(CreateKind.Member)) : null}
        />
      )}
    />
  )
}

export function BoardVisitorsWidget() {
  const t = useTranslations("dashboard.home")
  const { hrefFor } = useCreateOverlay()
  const createAccess = useCreateKindAccess()
  return (
    <BoardWidget
      testId='visitors'
      render={(board, data) => (
        <RecentVisitorsTile
          visitors={board.recentVisitors.items}
          total={board.recentVisitors.total}
          visitorsLink={linkTo(t("visitors.link"), data.href("/members/visitors"))}
          addVisitor={data.can.editMembers && createAccess.isAllowed(CreateKind.Visitor) ? linkTo(t("visitors.add"), hrefFor(CreateKind.Visitor)) : null}
        />
      )}
    />
  )
}

export function BoardIntegrationWidget() {
  const t = useTranslations("people.members.overview.board.integration")
  return (
    <BoardWidget
      testId='integration'
      render={(board, data) => (
        <IntegrationTile
          integration={board.integration}
          groupsLink={linkTo(t("link"), data.href("/members/groups"))}
          profileHref={profileHref(data)}
        />
      )}
    />
  )
}

export function BoardToConfirmWidget() {
  const t = useTranslations("people.members.overview.board.toConfirm")
  return (
    <BoardWidget
      testId='to-confirm'
      render={(board, data) => (
        <ToConfirmTile
          toConfirm={board.toConfirm}
          pendingLink={linkTo(t("link"), withQuery(data.href("/members/list"), "segment=pending_confirmation"))}
          profileHref={profileHref(data)}
          confirm={data.confirm}
        />
      )}
    />
  )
}

export function BoardBirthdaysWidget() {
  return (
    <BoardWidget
      testId='birthdays'
      render={(board) => <BirthdaysTile birthdays={board.birthdaysThisWeek.items} total={board.birthdaysThisWeek.total} />}
    />
  )
}

export function BoardMixWidget() {
  const t = useTranslations("people.members.overview.board.mix")
  return (
    <BoardWidget
      testId='mix'
      render={(board, data) => (
        <PeopleMixTile
          gender={board.gender}
          age={board.age}
          subtitle={data.context === OverviewContext.Parish ? t("subtitleParish") : t("subtitle")}
        />
      )}
    />
  )
}

export function BoardProfilesWidget() {
  const t = useTranslations("dashboard.home")
  return (
    <BoardWidget
      testId='profiles'
      render={(board, data) => (
        <ProfilesTile profiles={board.profiles} membersLink={linkTo(t("members.link"), data.href("/members/list"))} />
      )}
    />
  )
}
